import { useDispatch, useSelector } from 'react-redux';
import {
  putClientCalculationSettingsByClientIdRequest,
  selectClient,
} from '../model';
import { FormErrorAlert } from './FormErrorAlert';
import { useEffect, useState } from 'react';
import { DialogModal } from './DialogModal';
import { PercentInput } from './NumericInput';
import {
  appliedHealthCareInflation,
  areNumbersClose,
  defaultHealthcareInflation,
  isNullOrUndefined,
} from '@shared';
import { Button } from './ui/button';
import { HealthcareInflationCitation } from './HealthcareInflationCitation';

interface EditHealthCareInflationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  } else {
    return <p className="ml-2 text-sm text-red-400">{message}</p>;
  }
};

export default function EditHealthCareInflationModal({
  open,
  setOpen,
}: EditHealthCareInflationModalProps) {
  const client = useSelector(selectClient);
  const dispatch = useDispatch();

  const [inflationRate, setInflationRate] = useState<number>(
    appliedHealthCareInflation(client),
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);

  const handleChangeInflationRate = (value: number) => {
    setInflationRate(value);
    if (formHasBeenSubmitted) {
      validateForm();
    }
  };

  const validateForm = () => {
    let isFormValid = true;
    const errors: Record<string, string> = {};

    if (inflationRate < 0 || inflationRate > 100) {
      errors.inflationRate = 'Inflation rate must be between 0 and 100';
    }

    if (inflationRate === null) {
      errors.inflationRate = 'Inflation rate is required';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      isFormValid = false;
    }
    return isFormValid;
  };

  const handleClickSubmit = () => {
    setFormHasBeenSubmitted(true);
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    const defaultRate = defaultHealthcareInflation(client);
    const healthCareInflationRate = areNumbersClose(inflationRate, defaultRate)
      ? null
      : inflationRate;

    dispatch(
      putClientCalculationSettingsByClientIdRequest({
        clientId: client.clientId,
        clientCalculationSettings: {
          healthCareInflationRate,
        },
      }),
    );
    setOpen(false);
  };

  const formIsValid = Object.keys(formErrors).length === 0;

  const syncFormFromAppliedRate = () => {
    setInflationRate(appliedHealthCareInflation(client));
    setFormErrors({});
    setFormHasBeenSubmitted(false);
  };

  const resetToDefaultRate = () => {
    setInflationRate(defaultHealthcareInflation(client));
    setFormErrors({});
    setFormHasBeenSubmitted(false);
  };

  const onClose = () => {
    setOpen(false);
    syncFormFromAppliedRate();
  };

  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [inflationRate]);

  useEffect(() => {
    if (open) {
      syncFormFromAppliedRate();
    }
  }, [open, client.clientId]);

  const modalContent = (
    <>
      <div className="relative pb-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 pt-7">
          <div>
            <PercentInput
              label={'Inflation Rate (Annual)'}
              placeholder="Enter inflation rate"
              value={inflationRate ?? null}
              onChange={handleChangeInflationRate}
              onPreview={handleChangeInflationRate}
            />
            <ErrorMessage message={formErrors['inflationRate']} />
          </div>
          <div className="pt-3">
            <HealthcareInflationCitation client={client} />
          </div>
        </div>
      </div>
      {!formIsValid && (
        <FormErrorAlert mainErrorMessage="There is an error with your submission">
          <li>
            <span className="font-bold">Invalid input:</span> Please enter a
            valid inflation rate.
          </li>
        </FormErrorAlert>
      )}
    </>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title="Edit Healthcare Inflation Rate"
      subTitle="Set the expected annual rate of healthcare cost inflation."
      width="md"
      primaryButton={
        <div className="mt-5 flex flex-row-reverse gap-2">
          <Button
            disabled={!formIsValid || isNullOrUndefined(inflationRate)}
            className="m:w-auto inline-flex w-full justify-center rounded-md px-8 py-2 text-lg"
            onClick={handleClickSubmit}
          >
            Save
          </Button>
          <Button
            variant="outline"
            className="inline-flex w-full justify-center rounded-md  px-8 py-2 text-lg  sm:w-auto"
            onClick={resetToDefaultRate}
          >
            Reset
          </Button>
        </div>
      }
    >
      {modalContent}
    </DialogModal>
  );
}
